import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from '@custom/Header/Header';
import { customStyle } from '@custom/Utils/utils';
import { useTheme } from '../../ui/src/contextProviders';
import { CameraBlue, downloadIconBlack } from '../../ui/assets/svgIcons/icon';
import { AppDispatch, RootState } from '@custom/store/store';
import getQualityIndicators from '@custom/rtk-apis/qualityIndicators';
import { closeQualityIndicatorsModal, clearQualityIndicatorsState } from '@custom/store/slices/qualityIndicators';
import { Icon, Loader } from '@ohif/ui';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { avgTATColumns, critialTestReportedColumns, delayedReportsModalityWiseColumns, percentageOfRedosColumns } from './utils';
import { Tooltip } from 'antd';

// ******* INTERFACE ******** //
interface RedoCountType {
  month?: string;
  percentageRedos?: string;
  reportsWithRedoCount?: number;
  totalReports?: number;
}

const QualityIndicators: React.FC = () => {
  // ***** DARK/LIGHT THEME ***** //
  const { isDarkMode } = useTheme();

  // ******* REDUX ******** //
  const dispatch = useDispatch<AppDispatch>();
  const { status, result, loading, error } = useSelector(
    (state: RootState) => state.getQualityIndicatorsResponse
  );

  // ****** STATE ******** //
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [qualityIndicators, setQualityIndicators] = useState<any[]>([]);
  const [redoData, setRedoData] = useState<RedoCountType[]>([]);
  const [averageTAT, setAverageTAT] = useState([]);
  const [delayedReports, setDelayedReports] = useState([]);
  const [criticalAppointments, setCriticalAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const tableRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const tabs = ['Redo Percentage', 'Avergae TAT', 'Delayed Reports', 'Critical Appointments'];

  // ***** USEEFFECT ***** //
  useEffect(() => {
    setRedoData([]);
    setAverageTAT([]);
    setDelayedReports([]);
    setCriticalAppointments([]);
    const payload = {
      year: selectedYear
    }
    dispatch(getQualityIndicators(payload));
  }, [selectedYear]);

  useEffect(() => {
    if (status === 'SUCCESS' && result) {
      setQualityIndicators(result);
      setRedoData(result?.result?.redoPercentageMonthlyData || []);
      setAverageTAT(result?.result?.avgTATModalityWise);
      setDelayedReports(result?.result?.delayedReportsModalityWise);
      setCriticalAppointments([result?.result?.appointmentSummary]);
    }
  }, [status, result]);

  // ***** Handler Functions ***** //
  const handleDownloadTable = async (name: string) => {
    const chartElement = tableRef.current;
    if (!chartElement) return;

    const downloadButton = chartElement?.querySelector('.btn__white');
    if (downloadButton) downloadButton.style.display = 'none';

    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('portrait');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.setFontSize(16);
    pdf.text(`${name} - ${selectedYear}`, 10, 15);

    // Add the table image below the title
    pdf.addImage(imgData, 'PNG', 10, 20, pdfWidth - 20, pdfHeight);

    // Show the button again after capturing
    if (downloadButton) downloadButton.style.display = 'block';

    pdf.save(`${name} ${selectedYear}.pdf`);
  };

  const tabConfigs = [
    {
      title: "Percentage of Re-dos",
      columns: percentageOfRedosColumns,
      data: redoData.map((item) => ({
        sno: item.month,
        month: getMonthName(item.month),
        totalReports: item.totalReports,
        reportsWithRedoCount: item.reportsWithRedoCount,
        percentageRedos: item.percentageRedos
      })) || [],
      downloadTitle: "Percentage of Re-dos Report",
      totalRowConfig: {
        columns: ['totalReports', 'reportsWithRedoCount'],
        label: 'Total',
        style: { fontWeight: 'bold' }
      }
    },
    {
      title: "Average TAT",
      columns: avgTATColumns,
      data: averageTAT.map((item, index) => ({
        sno: index + 1,
        modality: item.modality,
        totalReports: item.totalReports,
        totalAppointments: item.totalAppointments,
        avgTAT: item.avgTAT
      })) || [],
      downloadTitle: "Average TAT Report",
      totalRowConfig: {
        columns: ['totalReports', 'totalAppointments', 'avgTAT'],
        label: 'Total',
        style: { fontWeight: 'bold' }
      }
    },
    {
      title: "Delayed Reports",
      columns: delayedReportsModalityWiseColumns,
      data: delayedReports.map((item, index) => ({
        sno: index + 1,
        modality: item.modality,
        totalReports: item.totalPatientsWithReports,
        totalDelayedReports: item.totalDelayedReports,
        percentageDelayedReports: item.percentageDelayedReports
      })) || [],
      downloadTitle: "Delayed Reports",
      totalRowConfig: {
        columns: ['totalReports', 'totalDelayedReports'],
        label: 'Total',
        style: { fontWeight: 'bold' }
      }
    },
    {
      title: "Critical Appointments",
      columns: critialTestReportedColumns,
      data: criticalAppointments.map((item, index) => {
        return {
          sno: index + 1,
          criticalAppointments: item[index].criticalAppointments,
          currentMonthAppointmentResult: item[index].currentMonthAppointmentResult
        }
      }) || [],
      downloadTitle: "Critical Test Reported",
    }
  ];

  return (
    <div
      className="page page__full"
      style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}
    >
      <Header title="Quality Indicators" isBack={false} />
      <div className="page__body" style={{ paddingBottom: '0' }}>
        <div className='details ' style={{ paddingTop: '0' }}>
          <div className="flex justify-end" style={{ paddingBottom: '20px' }}>
            <YearSelector
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              currentYear={currentYear}
            />
          </div>
          <div className="tabs-container" style={{ maxHeight: "auto", overflow: "auto" }}>
            <QualitIndicatorsTabs
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            {tabConfigs[activeTab] && (
              <QualityIndicatorsTable
                title={tabConfigs[activeTab].title}
                columns={tabConfigs[activeTab].columns}
                tableRef={tableRef}
                data={tabConfigs[activeTab].data}
                onDownload={() => handleDownloadTable(tabConfigs[activeTab].downloadTitle)}
                totalRowConfig={tabConfigs[activeTab].totalRowConfig}
              />
            )}
          </div>
        </div>
      </div>
      <Loader isLoaderOpen={loading} />
    </div>
  );
};

export default QualityIndicators;


interface QualityIndicatorsTableProps {
  columns: { label: string; key: string }[];
  data: any[];
  title: string;
  tableRef?: React.RefObject<HTMLDivElement>;
  onDownload?: () => void;
  totalRowConfig?: { columns: string[]; label?: string; style?: React.CSSProperties };
}

const QualityIndicatorsTable = ({ columns, data, title, tableRef, onDownload, totalRowConfig } : QualityIndicatorsTableProps) => {
  const shouldShowTotalRow = totalRowConfig && totalRowConfig.columns?.length > 0;
  const getTotalValue = (key) => data?.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

  return (
    <div className="tableWrap">
      <div className='one-line'>
        <div className="flex items-center">
          <p style={{ fontSize: '20px', fontWeight: '500' }}>{title}</p>
          {/* <Tooltip
            arrow
            title={`This table contains ${data?.length} rows and ${columns?.length} columns.`}
            placement='right'
            color='#fff'
            className="ml-4"
          > i </Tooltip> */}
        </div>
        {onDownload && (
          <button className='btn btn__white btn--sm' onClick={onDownload}>
            <img className='w-[30px]' src={downloadIconBlack} alt="Download" />
          </button>
        )}
      </div>
      <div className='tableContainer' ref={tableRef}>
        <div className='tableContainer__wrap'>
          <table className='table'>
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th key={index}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <td key={colIndex}>{row[col.key] || '--'}</td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">No data available</td>
                </tr>
              )}
              {shouldShowTotalRow && (
                <tr className="total-row">
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} style={totalRowConfig.style || {}}>
                      {colIndex === 0 ? totalRowConfig.label || "Total" : totalRowConfig.columns.includes(col.key) ? getTotalValue(col.key) : ''}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const QualitIndicatorsTabs = ({ tabs, activeTab, setActiveTab }) => {
  return (
    <div className="tabs-header">
      {tabs.map((tab, index) => (
        <button
          key={index}
          className={`tab ${activeTab === index ? "active" : ""}`}
          onClick={() => setActiveTab(index)}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

const YearSelector = ({ selectedYear, setSelectedYear, currentYear }) => {
  return (
  <select id="year-select" className="year-dropdown" style={customStyle.select} value={selectedYear}  onChange={(e) => setSelectedYear(Number(e.target.value))}>
      {Array.from({ length: currentYear - 1945 + 1 }, (_, index) => 1945 + index).map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  )
};

const getMonthName = (month) => {
  const date = new Date();
  date.setMonth(month - 1);
  return date.toLocaleString('default', { month: 'long' });
};
